import React, { useState, useRef, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { ToWords } from 'to-words';
import QRCode from 'qrcode';
import { getStateFromGST } from '../../../../../utils/getStateFromGST';
import moment from 'moment';
import { Modal, Button, Checkbox } from 'antd';
import { BsDownload, BsEye } from "react-icons/bs";

/** staticInvoiceData uses "-" for missing values; skip so fallbacks match the next field from the same sources as lines 60–65. */
function firstCreditNoteDisplayField(values, emptyLabel) {
    for (const v of values) {
        if (v == null) continue;
        const s = String(v).trim();
        if (s !== "" && s !== "-") return s;
    }
    return emptyLabel;
}

const PdfFormatJsx = ({
    creditNoteData,
    openFiltersOnLoad = false,
    onRequestClose,
    showLauncherButtons = true,
}) => {
    const [showHeading, setShowHeading] = useState(true);
    const [showPeriod, setShowPeriod] = useState(true);
    // Matches GenerateSubInvoice behavior: if enabled, show total only (first line) and hide per-item amounts.
    const [showItemAmount, setShowItemAmount] = useState(false);
    const [showLogo, setShowLogo] = useState(true);
    const [showSeparateProductionCharges, setShowSeparateProductionCharges] = useState(false);
    const [showHoardingWisePrintingMounting, setShowHoardingWisePrintingMounting] =
        useState(false);
    const [showPrintingMountingOnly, setShowPrintingMountingOnly] = useState(false);

    const [isFiltersOpen, setIsFiltersOpen] = useState(
        Boolean(openFiltersOnLoad && creditNoteData)
    );
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const pdfPreviewRef = useRef(null);
    const [pdfUrl, setPdfUrl] = useState("");
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
    const [eInvoiceInfo, setEinvoiceInfo] = useState({});

    // console.log("creditNoteData:::::", creditNoteData);

    useEffect(() => {
        // Keep behavior stable if data arrives after mount.
        if (openFiltersOnLoad && creditNoteData) setIsFiltersOpen(true);
    }, [openFiltersOnLoad, creditNoteData]);

    const toWords = new ToWords({
        localeCode: "en-IN",
        converterOptions: {
            currency: false,
            ordinal: false,
        },
    });

    useEffect(() => {
        const generateQRCode = async () => {
            if (creditNoteData?.SignedQrCode) {
                try {
                    setEinvoiceInfo({
                        irn: creditNoteData?.IRN || "",
                        ackNo: creditNoteData?.AckNumber || "",
                        ackDate: creditNoteData?.AckDate || "",
                    });
                    const dataUrl = await QRCode.toDataURL(creditNoteData.SignedQrCode);
                    console.log("QR Code generated successfully");
                    setQrCodeDataUrl(dataUrl);
                } catch (error) {
                    console.error("Error generating QR code:", error);
                }
            }
        };
        generateQRCode();
    }, [creditNoteData]);

    const staticInvoiceData = {
        stamp: creditNoteData?.CnNumber || "-",
        billNumber: creditNoteData?.CnNumber || "-",
        againstBillNumber: creditNoteData?.invoiceId?.billNumber || "-",
        againstBillDate: creditNoteData?.invoiceId?.createdAt || "-",
        HSN: creditNoteData?.invoiceId?.HSN || "-",
        createdAt: creditNoteData?.createdAt || "-",
        refNo: creditNoteData?.invoiceId?.refNo || "-",
        refDate: creditNoteData?.invoiceId?.refDate || "-",
        partyName: creditNoteData?.party?.name || "-",
        partyAddress: creditNoteData?.party?.address || "-",
        brandName:
            creditNoteData?.brand?.brandName ||
            creditNoteData?.invoiceId?.brandName ||
            creditNoteData?.invoiceId?.salesOrderId?.bdfId?.brandId?.brandName ||
            "-",
        campaignName: creditNoteData?.invoiceId?.salesOrderId?.bdfId?.campaignName || "-",
        aliasPartyName: creditNoteData?.aliasPartyName || creditNoteData?.invoiceId?.salesOrderId?.bdfId?.parentCompanyName || "-",
        splitParentCompanyName: creditNoteData?.invoiceId?.billingId?.partyCompany?.name || "-",
        parentCompanyName: creditNoteData?.parentCompanyName || "-",
        splitParentCompanyAddress: creditNoteData?.invoiceId?.billingId?.partyCompany?.address || "-",
        parentCompanyAddress: creditNoteData?.invoiceId?.salesOrderId?.bdfId?.parentCompanyId?.address || "-",
        partyGstNumber: creditNoteData?.party?.gstNumber || "-",
        splitGSTNumber: creditNoteData?.invoiceId?.billingId?.partyCompany?.gstNumber || "-",
        parentCompanyGstNumber:
            creditNoteData?.invoiceId?.salesOrderId?.bdfId?.parentCompanyId
                ?.gstNumber || "-",
        gstNumber: creditNoteData?.gstNumber || "-",
        SGST: creditNoteData?.invoiceId?.SGST ?? "-",
        CGST: creditNoteData?.invoiceId?.CGST ?? "-",
        IGST: creditNoteData?.invoiceId?.IGST ?? "-",
        billedInvs: creditNoteData?.inventoryAgainst
            ?.filter((inv) => inv?.amount != null)
            ?.map((inv) => ({
                locationName: inv?.invId?.locations?.name || "-",
                size:
                    inv?.invId?.width && inv?.invId?.height
                        ? `${inv.invId.width} x ${inv.invId.height}`
                        : "-",
                startDate: inv?.startDate || "-",
                endDate: inv?.endDate || "-",
                amount: inv?.amount,
            })) || [
            {
                locationName: "-",
                size: "-",
                startDate: "-",
                endDate: "-",
                amount: "-"
            }
        ],
        printingCharge: creditNoteData?.printingCharge || "-",
        mountingCharge: creditNoteData?.mountingCharge || "-",
        isRejected: creditNoteData?.isRejected || false,
        SignedQRCode: creditNoteData?.SignedQrCode || "-",
        IRN: creditNoteData?.IRN || "-",
        AckNo: creditNoteData?.AckNumber || "-",
        AckDate: creditNoteData?.AckDate || "-"
    };

    const generatePDF = () => {
        const doc = new jsPDF("p", "mm", "a4");

        const drawCancelledWatermark = (doc) => {
            const pageCount = doc.getNumberOfPages();
            const watermarkText = "CANCELLED";

            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);

                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();

                doc.saveGraphicsState();

                doc.setTextColor(255, 80, 80);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(80);

                const offsetX = 25;
                const offsetY = 40;

                doc.text(
                    watermarkText,
                    pageWidth / 2 + offsetX,
                    pageHeight / 2 + offsetY,
                    {
                        angle: 45,
                        align: "center",
                        baseline: "middle",
                    }
                );

                doc.restoreGraphicsState();
            }
        };

        let startY = 70;
        const columnWidths = [10, 97, 16, 21, 21, 35];
        const rowHeight = 8;
        let headers = [];
        doc.setFont("helvetica", "bold").setFontSize(10);

        const addHeader = () => {
            // The full header (title, company, logo, QR/IRN, party and bill details) is drawn on every
            // page, same as the Tax Invoice.
            doc.setFont("helvetica", "bold").setFontSize(10);
            doc.text("CREDIT NOTE", 10, 25, { maxWidth: 100 });

            doc.setFontSize(8);
            doc.text("(Original for Buyer)", 35, 25);

            doc.setFontSize(14).setFont("helvetica", "bold");
            if (showHeading) {
                doc.text("Yellow Spot OOH", 10, 30, { maxWidth: 100 });
            }
            doc.setFont("helvetica", "normal").setFontSize(10);

            const headerAddress = `12th Block, 4th Floor, Magnet Corporate Park, Opp. Grand Cambay, Thaltej, Ahmedabad-380054.`;
            const headerAddressLines = doc.splitTextToSize(headerAddress, 100);
            doc.text(headerAddressLines, 10, 35);

            const headerAddressHeight = headerAddressLines.length * 5;

            let contentStartY = 30 + headerAddressHeight + 5;

            doc.setFont("helvetica", "bold");
            doc.text("Phone No. : 9033001770", 10, contentStartY);
            contentStartY += 5;
            doc.text(
                "GST No. : 24AADFY0077E1ZE, PAN No. : AADFY0077E",
                10,
                contentStartY
            );
            contentStartY += 5;
            doc.text("MSME No. : UDYAM-GJ-01-0215162", 10, contentStartY);
            doc.setFont("helvetica", "normal");

            if (showLogo) {
                doc.addImage("/images/pptSlide11.png", "PNG", 165, 20, 35, 35);
            }
            if (staticInvoiceData.SignedQRCode) {
                doc.addImage(qrCodeDataUrl, "PNG", 125, 20, 36, 36);
            }

            if (staticInvoiceData.SignedQRCode) {
                const newRectX = 10;
                const newRectY = 70 - 11; // fixed: just above the party / bill details boxes
                const newRectWidth = 190;
                const newRectHeight = 10;

                doc.line(newRectX, newRectY, newRectX, newRectY + newRectHeight);
                doc.line(
                    newRectX + newRectWidth,
                    newRectY,
                    newRectX + newRectWidth,
                    newRectY + newRectHeight
                );

                doc.line(newRectX, newRectY, newRectX + newRectWidth, newRectY);
                doc.line(
                    newRectX,
                    newRectY + newRectHeight,
                    newRectX + newRectWidth,
                    newRectY + newRectHeight
                );

                doc.setFont("helvetica", "bold").setFontSize(8);
                doc.text(
                    `IRN: ${staticInvoiceData?.IRN || "N/A"}`,
                    newRectX + 5,
                    newRectY + 4
                );
                doc.text(
                    `Ack.No: ${staticInvoiceData?.AckNo || "N/A"}`,
                    newRectX + 5,
                    newRectY + 8
                );
                doc.text(
                    `Ack.Date: ${staticInvoiceData?.AckDate || "N/A"}`,
                    newRectX + 70,
                    newRectY + 8
                );
            }

            let addressLeftY = 70;
            const gstNumber = firstCreditNoteDisplayField(
                [
                    staticInvoiceData?.partyGstNumber,
                    staticInvoiceData?.splitGSTNumber,
                    staticInvoiceData?.parentCompanyGstNumber,
                    staticInvoiceData?.gstNumber,
                ],
                "NA"
            );
            const panFromGst = gstNumber !== "NA" && gstNumber.length >= 15
                ? gstNumber.substring(2, 12)
                : "NA";

            const stateCode = gstNumber !== "NA" ? gstNumber.substring(0, 2) : "NA";
            const stateName = getStateFromGST(gstNumber);

            const companyName = firstCreditNoteDisplayField(
                [
                    staticInvoiceData?.partyName,
                    staticInvoiceData?.splitParentCompanyName,
                    staticInvoiceData?.aliasPartyName,
                    staticInvoiceData?.parentCompanyName,
                ],
                "N/A"
            );

            const companyAddress = firstCreditNoteDisplayField(
                [
                    staticInvoiceData?.partyAddress,
                    staticInvoiceData?.splitParentCompanyAddress,
                    staticInvoiceData?.parentCompanyAddress,
                ],
                "N/A"
            );

            const addressLeftText = [
                "To,",
                companyName,
                companyAddress,
                `State : ${stateName}, Code : ${stateCode}`,
                `GST No. : ${gstNumber}`,
                `PAN No. : ${panFromGst}`,
            ];

            const wrappedAddressLeftText = doc.splitTextToSize(addressLeftText, 90);
            const lineHeight = 5;
            const addressLeftHeight = wrappedAddressLeftText.length * lineHeight + 1;

            wrappedAddressLeftText.forEach((line, index) => {
                if (
                    index === 1 ||
                    index === wrappedAddressLeftText.length - 1 ||
                    index === wrappedAddressLeftText.length - 2
                ) {
                    doc.setFont("helvetica", "bold");
                } else {
                    doc.setFont("helvetica", "normal");
                }

                const currentYPosition = addressLeftY + index * lineHeight;
                doc.text(line, 15, currentYPosition + 5);
            });

            const displayBrandName = firstCreditNoteDisplayField(
                [
                    creditNoteData?.brand?.brandName,
                    creditNoteData?.invoiceId?.brandName,
                    creditNoteData?.invoiceId?.salesOrderId?.bdfId?.brandId
                        ?.brandName,
                ],
                "NA"
            );

            const addressRightY = addressLeftY;
            const addressRightText = [
                `Credit Note No. : ${staticInvoiceData?.stamp
                    ? staticInvoiceData?.stamp
                    : staticInvoiceData?.billNumber
                        ? staticInvoiceData?.billNumber
                        : "NA"
                }`,
                `Credit Note Date : ${moment(staticInvoiceData?.createdAt).format(
                    "DD-MM-YYYY"
                )}`,
                `Against Bill No. : ${staticInvoiceData?.againstBillNumber ? staticInvoiceData?.againstBillNumber : ""}`,
                `Against Bill Date : ${staticInvoiceData?.againstBillDate
                    ? moment(staticInvoiceData?.againstBillDate).format("DD-MM-YYYY")
                    : ""
                }`,
                `Brand Name : ${displayBrandName}`,
                `Place of Supply : Gujarat`,
                `Reverse Charge : N`,
            ];
            const addressRightHeight = addressRightText.length * 5 + 4;

            const maxHeight = Math.max(addressLeftHeight, addressRightHeight);

            doc.rect(10, addressLeftY, 95, maxHeight);
            doc.rect(105, addressRightY, 95, maxHeight);

            doc.setFont("helvetica", "bold");
            addressRightText.forEach((line, index) => {
                doc.text(line, 110, addressRightY + 5 + index * 5);
            });
            doc.setFont("helvetica", "normal");

            startY = addressLeftY + maxHeight;

            // Table header is shown on all pages
            doc.rect(10, startY, 190, rowHeight);

            headers = ["Sr", "Media/Location", "Size"];
            if (showPeriod) {
                headers.push("From Date", "To Date");
            } else {
                headers.push("", "");
            }
            headers.push("Amount");

            doc.setFont("helvetica", "bold");
            headers.forEach((header, i) => {
                const position = 12 + columnWidths.slice(0, i).reduce((a, b) => a + b, 0);
                doc.text(header, position, startY + 5);
            });

            doc.setFont("helvetica", "normal");
            startY += rowHeight;
        };

        addHeader();

        const rows = [];

        rows.push([
            "",
            `Being the charges for Advertisement through display your ad on Outdoor Hoarding sites at`,
            "",
            "",
            "",
            "",
        ]);

        const totalProductionCharges = {
            printingCharge: Number(staticInvoiceData?.printingCharge) || 0,
            mountingCharge: Number(staticInvoiceData?.mountingCharge) || 0,
        };

        const totalProductionCost =
            (Number(totalProductionCharges?.printingCharge) || 0) +
            (Number(totalProductionCharges?.mountingCharge) || 0);

        const totalSpecialRate = staticInvoiceData?.billedInvs?.reduce(
            (sum, inventory) => sum + (Number(inventory?.amount) || 0),
            0,
        );

        const effectiveSpecialRate = showPrintingMountingOnly ? 0 : totalSpecialRate;
        const total = effectiveSpecialRate + totalProductionCost;
        const totalFormatted = (Number(total) || 0).toFixed(2);
        const totalFormattedDisplay = new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(total);

        const billedInvList = staticInvoiceData?.billedInvs || [];
        const billedAmountTotal = billedInvList.reduce(
            (s, inv) => s + (Number(inv?.amount) || 0),
            0
        );
        const formatterAmounts = new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        // Build inventory rows (and optionally production rows) based on filters
        if (!showPrintingMountingOnly) {
            if (showHoardingWisePrintingMounting) {
                let srNo = 0;
                billedInvList.forEach((inventory) => {
                    const weight =
                        billedAmountTotal > 0
                            ? (Number(inventory?.amount) || 0) / billedAmountTotal
                            : billedInvList.length > 0
                                ? 1 / billedInvList.length
                                : 1;

                    const printingCharge = (Number(totalProductionCharges.printingCharge) || 0) * weight;
                    const mountingCharge = (Number(totalProductionCharges.mountingCharge) || 0) * weight;

                    const locationName = inventory?.locationName || "-";
                    const sizeStr = inventory?.size || "-";
                    const fromDate = moment(inventory.startDate).format("DD-MM-YYYY");
                    const toDate = moment(inventory.endDate).format("DD-MM-YYYY");

                    srNo += 1;
                    rows.push([
                        `${srNo}`,
                        `${locationName} - Printing Charge`,
                        sizeStr,
                        fromDate,
                        toDate,
                        showItemAmount && srNo === 1 ? totalFormattedDisplay : showItemAmount ? "" : formatterAmounts.format(printingCharge),
                    ]);
                    srNo += 1;
                    rows.push([
                        `${srNo}`,
                        `${locationName} - Mounting Charge`,
                        sizeStr,
                        fromDate,
                        toDate,
                        showItemAmount ? "" : formatterAmounts.format(mountingCharge),
                    ]);
                });
            } else {
                rows.push(
                    ...(billedInvList.map((inventory, index) => [
                        `${index + 1}`,
                        inventory?.locationName || "-",
                        inventory?.size || "-",
                        moment(inventory.startDate).format("DD-MM-YYYY"),
                        moment(inventory.endDate).format("DD-MM-YYYY"),
                        showItemAmount && index === 0
                            ? totalFormattedDisplay
                            : showItemAmount
                                ? ""
                                : formatterAmounts.format(Number(inventory?.amount) || 0),
                    ]) || [])
                );
            }
        }

        // Production charges rows (skip when hoarding-wise is enabled since it is already per-row)
        // Use BDF date only when this credit note is from Sub BDF.
        const isFromSubBdf = Boolean(creditNoteData?.invoiceId?.salesOrderId?.isFromSub);
        const bdfDateRaw = creditNoteData?.invoiceId?.salesOrderId?.bdfId?.bdfDate;
        const bdfDateStr = (() => {
            if (!bdfDateRaw) return "";
            if (typeof bdfDateRaw === "string") {
                const trimmed = bdfDateRaw.trim();
                if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) return trimmed; // already DD-MM-YYYY
                const parsed = moment(trimmed, ["DD-MM-YYYY", moment.ISO_8601], true);
                return parsed.isValid() ? parsed.format("DD-MM-YYYY") : trimmed;
            }
            return moment(bdfDateRaw).format("DD-MM-YYYY");
        })();
        const productionFromDate = showPeriod ? (isFromSubBdf ? bdfDateStr : "") : "";
        const productionToDate = productionFromDate;
        const nextSerialNumber = showPrintingMountingOnly
            ? 1
            : (showHoardingWisePrintingMounting ? billedInvList.length * 2 : billedInvList.length) + 1;

        if (!showHoardingWisePrintingMounting) {
            if (showSeparateProductionCharges) {
                if (Number(totalProductionCharges?.printingCharge) > 0) {
                    rows.push([
                        `${nextSerialNumber}`,
                        "Printing Charges",
                        "",
                        productionFromDate,
                        productionToDate,
                        showItemAmount ? "" : formatterAmounts.format(Number(totalProductionCharges?.printingCharge) || 0),
                    ]);
                }
                if (Number(totalProductionCharges?.mountingCharge) > 0) {
                    rows.push([
                        `${nextSerialNumber + (Number(totalProductionCharges?.printingCharge) > 0 ? 1 : 0)}`,
                        "Mounting Charges",
                        "",
                        productionFromDate,
                        productionToDate,
                        showItemAmount ? "" : formatterAmounts.format(Number(totalProductionCharges?.mountingCharge) || 0),
                    ]);
                }
            } else if (Number(totalProductionCost) > 0) {
                rows.push([
                    `${nextSerialNumber}`,
                    "Printing & Mounting Charges",
                    "",
                    productionFromDate,
                    productionToDate,
                    showItemAmount ? "" : formatterAmounts.format(Number(totalProductionCost) || 0),
                ]);
            }
        }

        // Pad with empty rows at the end (not after the description line),
        // to keep layout stable like GenerateSubInvoice.
        if (rows.length <= 4) {
            rows.push(...Array(2).fill(["", "", "", "", "", ""]));
        }

        const totalAmount = parseFloat(totalFormatted);
        const sgstAmount = (totalAmount * (staticInvoiceData?.SGST || 0)) / 100;
        const cgstAmount = (totalAmount * (staticInvoiceData?.CGST || 0)) / 100;
        const igstAmount = (totalAmount * (staticInvoiceData?.IGST || 0)) / 100;

        const formatter = new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        const sgstAmountValue = parseFloat((Number(sgstAmount) || 0).toFixed(2));
        const cgstAmountValue = parseFloat((Number(cgstAmount) || 0).toFixed(2));
        const igstAmountValue = parseFloat((Number(igstAmount) || 0).toFixed(2));

        const sumBeforeRounding =
            totalAmount + sgstAmountValue + cgstAmountValue + igstAmountValue;

        const desiredGrandTotal = Math.round(sumBeforeRounding);
        const roundTaxAmount = desiredGrandTotal - sumBeforeRounding;
        const roundAmountValue = parseFloat((Number(roundTaxAmount) || 0).toFixed(2));

        const sgstAmountFormatted = formatter.format(sgstAmountValue);
        const cgstAmountFormatted = formatter.format(cgstAmountValue);
        const igstAmountFormatted = formatter.format(igstAmountValue);
        const roundAmountFormatted = formatter.format(roundAmountValue);

        doc.setFontSize(9);

        let newSectionY;
        let newSectionHeight;

        const maxRowsPerPage = 8;
        let rowCount = 0;
        const leftX = 10;
        const rightX = 200;
        const divider1X = leftX + columnWidths[0];
        const divider2X = leftX + columnWidths.slice(0, -1).reduce((a, b) => a + b, 0);

        rows.forEach((row, rowIndex) => {
            if (rowCount >= maxRowsPerPage) {
                addSummarySection(false);
                addFooter();

                doc.addPage();
                addHeader();
                startY = startY || 70;
                rowCount = 0;
            }

            let currentX = 10;
            let rowTotalHeight = rowHeight;

            row.forEach((text, index) => {
                if (rowIndex === 0) {
                    const staticTextWidth = 150;
                    const wrappedText =
                        doc.getTextWidth(text) > staticTextWidth
                            ? doc.splitTextToSize(text, staticTextWidth)
                            : [text];

                    rowTotalHeight = Math.max(rowTotalHeight, wrappedText.length * 5);
                    const textY =
                        startY + rowTotalHeight / 2 - (wrappedText.length * 5) / 2 + 3;

                    wrappedText.forEach((line, lineIndex) => {
                        doc.text(line, currentX + 2, textY + lineIndex * 5 + 2);
                    });

                    currentX += columnWidths[index];
                    return;
                }

                if (!showPeriod && (index === 3 || index === 4)) {
                    currentX += columnWidths[index];
                    return;
                }

                const wrappedText = doc.splitTextToSize(text, columnWidths[index] - 3);
                rowTotalHeight = Math.max(rowTotalHeight, wrappedText.length * 5);

                const textY =
                    startY + rowTotalHeight / 2 - (wrappedText.length * 5) / 2 + 3;

                wrappedText.forEach((line, lineIndex) => {
                    if (index === row.length - 1) {
                        doc.text(
                            line,
                            rightX - 3,
                            textY + lineIndex * 5 + 2,
                            { align: "right", maxWidth: rightX - divider2X - 6 }
                        );
                    } else {
                        doc.text(line, currentX + 2, textY + lineIndex * 5 + 2);
                    }
                });

                currentX += columnWidths[index];
            });

            doc.line(
                divider1X,
                startY,
                divider1X,
                startY + rowTotalHeight + 2
            );

            doc.line(
                divider2X,
                startY,
                divider2X,
                startY + rowTotalHeight + 2
            );

            doc.line(rightX, startY, rightX, startY + rowTotalHeight + 2);
            doc.line(leftX, startY, leftX, startY + rowTotalHeight + 2);

            startY += rowTotalHeight + 2;
            rowCount++;
        });

        // Keep the inventory/table section height consistent on the last page.
        // Fill the remaining rows with empty rows so the summary starts at a fixed Y.
        while (rowCount < maxRowsPerPage) {
            const emptyRowHeight = rowHeight + 2;
            doc.line(divider1X, startY, divider1X, startY + emptyRowHeight);
            doc.line(divider2X, startY, divider2X, startY + emptyRowHeight);
            doc.line(rightX, startY, rightX, startY + emptyRowHeight);
            doc.line(leftX, startY, leftX, startY + emptyRowHeight);
            startY += emptyRowHeight;
            rowCount++;
        }

        doc.setFontSize(10);
        doc.line(leftX, startY, rightX, startY);

        addSummarySection(true);

        function addSummarySection(isLastPage = false) {
            const firstSectionY = startY;
            const firstSectionHeight = isLastPage ? 28 : 0;
            doc.rect(10, firstSectionY, 95, firstSectionHeight);

            if (isLastPage) {
                doc.setFont("helvetica", "bold");
                doc.text("GST Category :  Advertising Agency", 15, firstSectionY + 10);
                doc.text(`HSN/SAC : ${staticInvoiceData?.HSN}`, 15, firstSectionY + 15);
                doc.setFont("helvetica", "normal");
            }

            const secondSectionY = firstSectionY;
            const maxHeight2 = firstSectionHeight;
            const secondSectionX = 105;
            const thirdSectionX = 175;
            doc.rect(secondSectionX, secondSectionY, 70, maxHeight2);
            doc.rect(thirdSectionX, secondSectionY, 25, maxHeight2);

            if (isLastPage) {
                let currentY = secondSectionY + 5;
                doc.text("Total", secondSectionX + 5, currentY);
                doc.text(totalFormattedDisplay, thirdSectionX + 23, currentY, { align: "right" });
                currentY += 5;

                // SGST
                if (Number(staticInvoiceData?.SGST) > 0) {
                    doc.text(`SGST : ${staticInvoiceData.SGST} %`, secondSectionX + 5, currentY);
                    doc.text(sgstAmountFormatted, thirdSectionX + 23, currentY, { align: "right" });
                    currentY += 5;
                }

                // CGST
                if (Number(staticInvoiceData?.CGST) > 0) {
                    doc.text(`CGST : ${staticInvoiceData.CGST} %`, secondSectionX + 5, currentY);
                    doc.text(cgstAmountFormatted, thirdSectionX + 23, currentY, { align: "right" });
                    currentY += 5;
                }

                // IGST
                if (Number(staticInvoiceData?.IGST) > 0) {
                    doc.text(`IGST : ${staticInvoiceData.IGST} %`, secondSectionX + 5, currentY);
                    doc.text(igstAmountFormatted, thirdSectionX + 23, currentY, { align: "right" });
                    currentY += 5;
                }

                if (roundAmountFormatted !== "0.00") {
                    doc.text(`Round off`, secondSectionX + 5, currentY);
                    doc.text(roundAmountFormatted, thirdSectionX + 23, currentY, { align: "right" });
                }
            }

            if (isLastPage) {
                doc.line(
                    secondSectionX,
                    secondSectionY + 21.5,
                    secondSectionX + 70,
                    secondSectionY + 21.5
                );
                doc.line(
                    thirdSectionX,
                    secondSectionY + 21.5,
                    thirdSectionX + 25,
                    secondSectionY + 21.5
                );
            }

            if (isLastPage) {
                const grandTotal = desiredGrandTotal;
                const formattedGrandTotal = formatter.format(grandTotal);

                doc.setFont("helvetica", "bold");
                doc.text("Grand Total", secondSectionX + 5, secondSectionY + 26);
                doc.text(formattedGrandTotal, thirdSectionX + 23, secondSectionY + 26, { align: "right" });
                doc.setFont("helvetica", "normal");

                const grandTotalInWords = toWords.convert(grandTotal);
                const formattedGrandTotalInWords = grandTotalInWords
                    .toLowerCase()
                    .split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ");

                newSectionY = secondSectionY + maxHeight2;
                newSectionHeight = 10;

                doc.rect(10, newSectionY, 190, newSectionHeight);

                doc.setFont("helvetica", "bold");
                doc.text("Rupees :-", 15, newSectionY + 7);
                doc.setFont("helvetica", "normal");
                doc.text(formattedGrandTotalInWords + " Only", 35, newSectionY + 7);

                doc.line(
                    10,
                    newSectionY + newSectionHeight,
                    200,
                    newSectionY + newSectionHeight
                );
            } else {
                newSectionY = secondSectionY + maxHeight2;
                newSectionHeight = 0;
            }
        }

        function addFooter() {
            const finalSectionY = newSectionY + newSectionHeight;

            doc.setFont("helvetica", "bold").setFontSize(10);
            doc.text("Terms & Conditions:", 15, finalSectionY + 5);

            const termsText = [
                "1. Cheque & NEFT/RTGS Should be in favour of 'Yellow Spot OOH'.",
                "2. Please do not Deduct TDS On GST.",
                "3. Payment Immediately after issuing this bill.",
                "4. Interest at 18% per annum will be charged on overdue accounts from the date of this bill.",
                "5. Subject to Ahmedabad Jurisdiction.",
                "6. Once ordered should not be cancelled.",
            ];

            doc.setFont("helvetica", "normal").setFontSize(8);
            const lineSpacing = 2.8;
            const pointSpacing = 1;

            let currentY = finalSectionY + 10;
            termsText.forEach((text, index) => {
                const wrappedText = doc.splitTextToSize(text, 100);

                wrappedText.forEach((line, lineIndex) => {
                    doc.text(line, 15, currentY);
                    currentY += lineSpacing;
                });

                currentY += pointSpacing;
            });

            const bankDetailsY = currentY + 5;
            doc
                .setFont("helvetica", "bold")
                .text("BANK DETAILS : AXIS BANK LTD.", 15, bankDetailsY);
            doc
                .setFont("helvetica", "bold")
                .text(
                    "A/C NO. 922020030481942, IFSC CODE: UTIB0002998",
                    15,
                    bankDetailsY + 5
                );

            const leftSectionHeight = bankDetailsY - finalSectionY + 10;

            doc.rect(10, finalSectionY, 115, leftSectionHeight);

            doc.rect(125, finalSectionY, 75, leftSectionHeight);
            doc
                .setFont("helvetica", "bold")
                .text("For, Yellow Spot OOH", 150, finalSectionY + 8);
            doc
                .setFont("helvetica", "bold")
                .text("[Authorised Signatory]", 150, bankDetailsY + 5);
            doc.addImage(
                "/images/yellowspotStamp.png",
                "PNG",
                150,
                finalSectionY + 10,
                30,
                30
            );
        }

        addFooter();

        if (staticInvoiceData?.isRejected) {
            drawCancelledWatermark(doc);
        }

        return doc;
    };

    const handleGeneratePDF = () => {
        const doc = generatePDF();
        if (doc) {
            const partyName = firstCreditNoteDisplayField(
                [
                    staticInvoiceData?.partyName,
                    staticInvoiceData?.aliasPartyName,
                    staticInvoiceData?.splitParentCompanyName,
                    staticInvoiceData?.parentCompanyName,
                ],
                "Client"
            );

            const creditNoteNumber = staticInvoiceData?.billNumber || staticInvoiceData?.stamp || "";
            const campaignName = staticInvoiceData?.campaignName || "";

            // Match GenerateSubInvoice naming, but use CreditNote as type.
            const fileName = `${creditNoteNumber}_${partyName}_${campaignName}_CreditNote.pdf`;
            doc.save(fileName);
        }
    };

    const handlePreview = () => {
        const doc = generatePDF();
        if (doc) {
            const pdfBlob = doc.output("blob");
            if (pdfBlob) {
                const nextPdfUrl = URL.createObjectURL(pdfBlob);
                setPdfUrl(nextPdfUrl);
                setIsPreviewOpen(true);
            }
        }
    };

    useEffect(() => {
        if (isPreviewOpen && pdfUrl && pdfPreviewRef.current) {
            pdfPreviewRef.current.src = pdfUrl;
        }
    }, [isPreviewOpen, pdfUrl]);

    useEffect(() => {
        return () => {
            if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        };
    }, [pdfUrl]);

    return (
        <div>
            {showLauncherButtons && (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "20px", margin: "50px 0px" }}>
                    <Button type="primary" onClick={() => setIsFiltersOpen(true)}>Download PDF</Button>
                    <Button type="primary" onClick={() => setIsFiltersOpen(true)}>Preview PDF</Button>
                </div>
            )}

            
            <Modal
                title={<div style={{ marginBottom: "2rem" }}>Invoice Filters</div>}
                open={isFiltersOpen}
                onCancel={() => {
                    setIsFiltersOpen(false);
                    onRequestClose?.();
                }}
                width={600}
                footer={[
                    <Button key="pdf" type="primary" onClick={handleGeneratePDF}>
                        <BsDownload style={{ marginRight: "8px" }} /> PDF
                    </Button>,
                    <Button key="preview" type="primary" onClick={handlePreview}>
                        <BsEye style={{ marginRight: "8px" }} /> Preview
                    </Button>,
                ]}
            >
                <div>
                    <Checkbox
                        checked={showLogo}
                        onChange={(e) => setShowLogo(e.target.checked)}
                        style={{ marginBottom: "10px" }}
                    >
                        Show logo
                    </Checkbox>
                </div>
                <div>
                    <Checkbox
                        checked={showPeriod}
                        onChange={(e) => setShowPeriod(e.target.checked)}
                        style={{ marginBottom: "10px" }}
                    >
                        Show period
                    </Checkbox>
                </div>
                <div>
                    <Checkbox
                        checked={showItemAmount}
                        onChange={(e) => setShowItemAmount(e.target.checked)}
                        style={{ marginBottom: "10px" }}
                    >
                        Show total billing amount only
                    </Checkbox>
                </div>
                <div>
                    <Checkbox
                        checked={showSeparateProductionCharges}
                        onChange={(e) => setShowSeparateProductionCharges(e.target.checked)}
                        style={{ marginBottom: "10px" }}
                    >
                        Show printing and mounting separately
                    </Checkbox>
                </div>
                <div>
                    <Checkbox
                        checked={showHoardingWisePrintingMounting}
                        onChange={(e) => setShowHoardingWisePrintingMounting(e.target.checked)}
                        style={{ marginBottom: "10px" }}
                    >
                        Show hoarding wise printing & mounting
                    </Checkbox>
                </div>
                <div>
                    <Checkbox
                        checked={showPrintingMountingOnly}
                        onChange={(e) => {
                            const checked = e.target.checked;
                            setShowPrintingMountingOnly(checked);
                        }}
                        style={{ marginBottom: "10px" }}
                    >
                        Show printing & mounting only
                    </Checkbox>
                </div>
            </Modal>

            <Modal
                title="Credit Note Preview"
                open={isPreviewOpen}
                onCancel={() => setIsPreviewOpen(false)}
                footer={null}
                width="fit-content"
                centered
            >
                {isPreviewOpen && (
                    <iframe
                        ref={pdfPreviewRef}
                        style={{
                            width: "310mm",
                            height: "197mm",
                            border: "none",
                            margin: "0 auto",
                            display: "block",
                            boxShadow: "0 0 15px rgba(0, 0, 0, 0.1)",
                        }}
                    />
                )}
            </Modal>
        </div>
    );
};

export default PdfFormatJsx;
